<template>
  <v-card
    :to="reviewUrl"
    min-height="120"
    class="review-card mb-6"
    :ripple="false"
  >
    <v-container fluid fill-height class="pa-6">
      <v-row no-gutters>
        <div class="d-flex flex-grow-1">
          <h2 class="text-h6" :title="review.title">
            {{ review.title }}
          </h2>
          <v-spacer />
          <review-card-menu
            :archived="review.archived"
            @archive="$emit('archive')"
            @unarchive="$emit('unarchive')"
            @delete="$emit('delete')"
            @click.prevent
          />
        </div>
      </v-row>
      <v-row no-gutters class="mt-4">
        <div class="card-section-1">
          <review-card-info-item
            class="lead-reviewer"
            label="Lead Reviewer"
          >
            <div class="d-flex align-center text-h6">
              <researcher-avatar
                :researcher="review.leadReviewer.researcher"
                small
              />
              <span
                class="ml-1 text-body-2 grey--text text--darken-2"
              >
                {{ review.leadReviewer.researcher.name }}
              </span>
            </div>
          </review-card-info-item>
          <review-card-info-item
            v-if="hasSecondaryReviewers"
            label="Secondary Reviewers"
            class=""
          >
            <researcher-avatar-cluster
              :researchers="review.reviewers.map((r) => r.researcher)"
              :max="4"
            />
          </review-card-info-item>
        </div>
        <div class="card-section-2 flex-grow-1">
          <review-card-info-item-count
            label="Total References"
            :count="totalReferences"
          />
          <review-card-info-item-count
            label="Screening"
            :count="totalScreeningRemaining"
          />
          <review-card-info-item-count
            label="Full Text"
            :count="totalFullTextRemaining"
          />
          <review-card-info-item-count
            label="Data Extraction"
            :count="totalDataExtractionRemaining"
          />
        </div>
      </v-row>
    </v-container>
  </v-card>
</template>

<script>
import { Review } from '@/models';
import ReviewCardMenu from './ReviewCardMenu';
import ReviewCardInfoItem from './ReviewCardInfoItem';
import ReviewCardInfoItemCount from './ReviewCardInfoItemCount';
import ResearcherAvatar from '@/components/Common/ResearcherAvatar';
import ResearcherAvatarCluster from '@/components/Common/ResearcherAvatarCluster';

export default {
  name: 'ReviewCard',
  components: {
    ReviewCardMenu,
    ReviewCardInfoItem,
    ReviewCardInfoItemCount,
    ResearcherAvatar,
    ResearcherAvatarCluster,
  },
  props: {
    review: {
      type: Review,
      required: true,
    },
  },
  emits: ['archive', 'unarchive', 'delete'],
  computed: {
    reviewUrl() {
      return this.review?.id
        ? `/review/${this.review.id}`
        : '/reviews';
    },
    hasSecondaryReviewers() {
      return this.review.reviewers.length > 0;
    },
    totalReferences() {
      // Use uniqueReferencesCount which is available immediately
      return this.review.uniqueReferencesCount || 0;
    },
    totalScreeningRemaining() {
      if (!this.review.screeningProgressSummary) {
        return this.review.uniqueReferencesCount > 0 ? '...' : 0;
      }
      // Check if totalRemaining is a function
      if (
        typeof this.review.screeningProgressSummary.totalRemaining ===
        'function'
      ) {
        return this.review.screeningProgressSummary.totalRemaining();
      }
      // Fallback calculation if totalRemaining is not a function
      const summary = this.review.screeningProgressSummary;
      return (
        (summary.unscreenedCount || 0) +
        (summary.partiallyScreenedCount || 0)
      );
    },
    totalFullTextRemaining() {
      if (!this.review.fullTextReviewScreeningProgressSummary) {
        return this.review.uniqueReferencesCount > 0 ? '...' : 0;
      }
      // Check if totalRemaining is a function
      if (
        typeof this.review.fullTextReviewScreeningProgressSummary
          .totalRemaining === 'function'
      ) {
        return this.review.fullTextReviewScreeningProgressSummary.totalRemaining();
      }
      // Fallback calculation
      const summary = this.review
        .fullTextReviewScreeningProgressSummary;
      return (
        (summary.unscreenedCount || 0) +
        (summary.partiallyScreenedCount || 0)
      );
    },
    totalDataExtractionRemaining() {
      if (!this.review.dataExtractionProgressSummary) {
        return this.review.uniqueReferencesCount > 0 ? '...' : 0;
      }
      // Check if totalRemaining is a function
      if (
        typeof this.review.dataExtractionProgressSummary
          .totalRemaining === 'function'
      ) {
        return this.review.dataExtractionProgressSummary.totalRemaining();
      }
      // Fallback calculation
      const summary = this.review.dataExtractionProgressSummary;
      return (
        (summary.needExtractionCount || 0) +
        (summary.partiallyExtractedCount || 0)
      );
    },
  },
};
</script>

<style scoped>
.card-section-1 {
  width: 400px;
  display: grid;
  grid-template-columns: repeat(2, 150px);
  gap: 24px;
}
.card-section-2 {
  display: grid;
  grid-template-columns: repeat(4, fit-content(100px));
  gap: 24px;
}
</style>
